'use client';
import { useState, useCallback, useContext } from 'react';
import dynamic from 'next/dynamic';
import classNames from 'classnames';
import { FaArrowUpRightFromSquare, FaCaretDown, FaCaretUp } from 'react-icons/fa6';

import { formatRoundFixed } from '@benzinga/utils';
import { ErrorBoundary } from '@benzinga/core-ui';
import { SessionContext } from '@benzinga/session-context';
import { TrackingManager } from '@benzinga/tracking-manager';
import { DelayedQuoteWithLogo } from '../../api/market/utils';

import { formatSymbolForTradingViewWidget } from '@benzinga/charts-ui';
import { BlockLayout } from '../../home/components/BlockLayout';
import { MenuList } from './QuoteCard';

const TradingViewWidget = dynamic(
  () => import('@benzinga/charts-ui').then(module => ({ default: module.TradingViewWidget })),
  { ssr: false, loading: () => <div className="min-h-[280px]" /> },
);

type ChartRanges = '1D' | '5D' | '1M' | '3M' | '12M' | 'ALL';

const RANGES: { label: string; value: ChartRanges }[] = [
  { label: 'Day', value: '1D' },
  { label: 'Week', value: '5D' },
  { label: 'Month', value: '1M' },
  { label: 'Year', value: '12M' },
  { label: 'Max', value: 'ALL' },
];

export const MarketPulseChart = ({
  markets,
  variant = 'default',
  icon,
  showTickerNames = false,
  buttonLocation = 'homepage',
}: {
  markets: DelayedQuoteWithLogo[];
  variant?: 'default' | 'compact';
  icon?: React.ReactNode;
  showTickerNames?: boolean;
  buttonLocation?: 'homepage' | 'markets_page';
}) => {
  const session = useContext(SessionContext);
  const [chartRange, setChartRange] = useState<ChartRanges>('1D');
  const [selectedTicker, setSelectedTicker] = useState<DelayedQuoteWithLogo | undefined>(markets?.[0]);

  const trackingManager = session.getManager(TrackingManager);

  const handleTickerSelect = useCallback(
    (ticker: DelayedQuoteWithLogo) => {
      trackingManager.trackLinkEvent('click', {
        button_type: 'toggle',
        button_location: buttonLocation,
        button_id: 'market_pulse_ticker',
        button_label: ticker?.symbol ?? '',
      });
      setSelectedTicker(ticker);
    },
    [buttonLocation, trackingManager],
  );

  const handleRangeSelect = useCallback(
    (rangeValue: ChartRanges) => {
      const label = RANGES.find(range => range.value === rangeValue)?.label ?? rangeValue;
      trackingManager.trackLinkEvent('click', {
        button_type: 'toggle',
        button_location: buttonLocation,
        button_id: 'market_pulse_chart_duration',
        button_label: label,
      });
      setChartRange(rangeValue);
    },
    [buttonLocation, trackingManager],
  );

  return (
    <>
    <ErrorBoundary name="market-pulse-chart">
      <BlockLayout
        title="Markets Pulse"
        icon={icon}
        headerClassName={buttonLocation === 'homepage' ? 'uppercase' : undefined}
      >
        <div className="@container">
          {markets?.length > 0 && (
            <div className="grid grid-cols-2 @[740px]:grid-cols-4 gap-2 mb-4">
              {markets?.map((ticker, idx) => {
                const isSelected = ticker?.symbol === selectedTicker?.symbol;
                const symbol = ticker?.symbol ?? '';
                const quoteUrl = `/quote/${symbol}`;
                return (
                  <div
                    className={classNames(
                      'flex flex-col border border-[#CEDDF2] rounded-md hover:bg-white hover:shadow-sm p-2 cursor-pointer',
                      isSelected && 'bg-white shadow-sm',
                    )}
                    key={idx}
                    onClick={() => handleTickerSelect(ticker)}
                  >
                    {variant === 'compact' ? (
                      <>
                        <div className="flex flex-col mb-1">
                          <div className="flex justify-between items-center">
                            <span className="text-gray-900 font-bold text-sm">{ticker?.symbol}</span>
                            <a
                              href={quoteUrl}
                              target="_blank"
                              className="text-blue-500"
                              aria-label={`View ${ticker?.symbol} quote`}
                              data-action={`Market Pulse Chart Quote Link Click - ${ticker?.symbol}`}
                            >
                              <FaArrowUpRightFromSquare />
                            </a>
                          </div>
                          {showTickerNames && (
                            <span className="text-gray-500 text-xs truncate">
                              {ticker?.type === 'CRYPTO'
                                ? ticker?.description
                                : ticker?.issuerShortName || ticker?.name}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center justify-between mt-auto w-full">
                          <span className="text-lg font-bold text-gray-900">${ticker?.lastTradePrice?.toFixed(2)}</span>
                          <span
                            className={`px-1 py-0.5 rounded text-xs font-bold whitespace-nowrap flex items-center justify-center ${
                              ticker?.changePercent > 0 ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600'
                            }`}
                          >
                            {ticker?.changePercent > 0 ? (
                              <FaCaretUp className="inline mr-0.5" />
                            ) : (
                              <FaCaretDown className="inline mr-0.5" />
                            )}
                            {formatRoundFixed(ticker?.changePercent, 2)}%
                          </span>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="flex flex-col">
                          <div className="flex flex-row justify-between items-center font-bold">
                            <span>{ticker?.symbol}</span>
                            <a
                              href={quoteUrl}
                              target="_blank"
                              aria-label={`View ${ticker?.symbol} quote`}
                              data-action={`Market Pulse Chart Quote Link Click - ${ticker?.symbol}`}
                            >
                              <FaArrowUpRightFromSquare />
                            </a>
                          </div>
                          {showTickerNames && (
                            <span className="text-gray-500 text-xs font-normal truncate">
                              {ticker?.issuerShortName || ticker?.name}
                            </span>
                          )}
                        </div>
                        <div>
                          <span className="text-lg">${ticker?.lastTradePrice?.toFixed(2)}</span>
                          <span
                            className={`flex flex-row items-center text-sm ${
                              ticker?.changePercent > 0 ? 'text-green-500' : 'text-red-500'
                            }`}
                          >
                            {ticker?.changePercent > 0 ? <FaCaretUp /> : <FaCaretDown />}{' '}
                            {formatRoundFixed(ticker?.changePercent, 2)}%
                          </span>
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          <div className="min-h-[280px] mb-4">
            <div className="min-h-[280px] mb-4">
              <TradingViewWidget
                widgetProps={{
                  allow_symbol_change: true,
                  backgroundColor: '#F2F8FF',
                  height: 280,
                  hide_legend: true,
                  hide_side_toolbar: true,
                  hide_top_toolbar: true,
                  hide_volume: true,
                  range: chartRange,
                  style: 3,
                  symbol: formatSymbolForTradingViewWidget(selectedTicker),
                  theme: 'light',
                  withdateranges: false,
                }}
              />
            </div>
          </div>
          <MenuList currentItem={chartRange} items={RANGES} onClick={handleRangeSelect} whiteBg />
        </div>
      </BlockLayout>
    </ErrorBoundary>
    </>
  );
};
